import { useState } from 'react';
import { useCuttingStore } from '../state/useCuttingStore';
import type { GrooveAssignment, GrooveType } from '../model/types';

/**
 * Block 14 «Раскрой» — графический селектор пазов детали (PR #10b-1-GROOVE, CUT-3.3).
 *
 * Пиксельный порт `project.html:761-790` (#groove-modal) + логика `app.js:208-256`
 * (openGrooveDialog / cycleGrooveSide / saveGrooveDialog / sideLabelGroove). Сетка
 * `.edge-modal-grid` (та же, что у `EdgeModal`): 4 стороны (top/left/right/bottom)
 * вокруг центра «деталь», тап по стороне циклически Нет → V-паз → U-паз →
 * L-четверть → Нет. Цвета V/U/L — оранжевый/зелёный/янтарь (renderGrooveSvg).
 *
 * Прямой аналог `EdgeModal` (#10a): шелл `.sc-modal2-overlay` с × в шапке, ОК →
 * `updatePart(id, { grooves })` → стор dirty → `GrooveMiniSvg` в ячейке
 * перерисовывается из стора. Пазы — feature-local (IDB-local персист, см.
 * GrooveAssignmentSchema), в замороженный контракт не идут.
 *
 * @see Помощник — Прототип Раскроя/01-prototype-vanilla/project.html:761-790
 */

/** Сторона детали (порядок прототипа app.js:212: top/right/bottom/left). */
type Side = 'top' | 'right' | 'bottom' | 'left';

/** Цикл значения стороны (app.js:230): '' → v → u → l → ''. */
const NEXT: Record<string, GrooveType> = { '': 'v', v: 'u', u: 'l', l: '' };

/** Подпись стороны (app.js:236-238). Неизвестное → «Нет». */
function sideLabel(t: GrooveType): string {
  if (t === 'v') return 'V-паз';
  if (t === 'u') return 'U-паз';
  if (t === 'l') return 'L-четв.';
  return 'Нет';
}

/** Человеко-читаемая сторона (для aria-label). */
const SIDE_RU: Record<Side, string> = {
  top: 'сверху',
  right: 'справа',
  bottom: 'снизу',
  left: 'слева',
};

const EMPTY_GROOVES: GrooveAssignment = { top: '', right: '', bottom: '', left: '' };

/** Нормализация модельного значения к циклу селектора (v/u/l/''). */
function normalize(t: GrooveType): GrooveType {
  return t === 'v' || t === 'u' || t === 'l' ? t : '';
}

export function GrooveModal({
  partId,
  partLabel,
  onClose,
}: {
  partId: string;
  partLabel: number;
  onClose: () => void;
}) {
  const updatePart = useCuttingStore((s) => s.updatePart);
  // Начальное состояние снимаем из стора один раз (модалка — транзиентный шаг).
  const [grooves, setGrooves] = useState<GrooveAssignment>(() => {
    const part = useCuttingStore.getState().document.parts.find((p) => p.id === partId);
    const g = part?.grooves ?? EMPTY_GROOVES;
    return {
      top: normalize(g.top),
      right: normalize(g.right),
      bottom: normalize(g.bottom),
      left: normalize(g.left),
    };
  });

  const cycle = (side: Side) => {
    setGrooves((prev) => ({ ...prev, [side]: NEXT[prev[side]] ?? 'v' }));
  };

  const apply = () => {
    updatePart(partId, { grooves });
    onClose();
  };

  const sideBtn = (side: Side) => {
    const t = grooves[side];
    const typeClass = t ? ` ${t}-groove` : '';
    return (
      <button
        type="button"
        className={`side ${side}${typeClass}`}
        aria-label={`Паз ${SIDE_RU[side]}: ${sideLabel(t)}`}
        onClick={() => cycle(side)}
      >
        {sideLabel(t)}
      </button>
    );
  };

  return (
    <div
      className="sc-modal2-overlay visible"
      role="dialog"
      aria-modal="true"
      aria-label={`Пазы детали ${partLabel}`}
    >
      <div className="sc-modal2">
        <div className="sc-modal2-title">
          <span>Пазы детали</span>
          <button type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div className="sc-modal2-content">
          <p className="edge-modal-hint">Укажите стороны с пазами</p>
          <div className="edge-modal-grid">
            {sideBtn('top')}
            {sideBtn('left')}
            <div className="edge-modal-center">деталь</div>
            {sideBtn('right')}
            {sideBtn('bottom')}
          </div>
          <div className="edge-modal-legend">
            Тап по стороне циклически: Нет → V-паз → U-паз → L-четверть → Нет
          </div>
        </div>
        <div className="sc-modal2-footer">
          <button type="button" className="sc-modal-btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="sc-modal-btn primary" onClick={apply}>
            Применить
          </button>
        </div>
      </div>
    </div>
  );
}
